import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Dialog } from 'primereact/dialog';
import { FiSearch, FiFileText, FiClock, FiMessageSquare, FiRefreshCw, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import './CommissionScan.css';
import CSVDownload from '../../assets/images/csv-download.svg'
import XMLDownload from '../../assets/images/xml-download.svg'
import PrivewIcon from '../../assets/images/ViewDetailsIcon.svg';
import ImportCSVIcon from '../../assets/images/import_CSV.svg';
import ImportXMLIcon from '../../assets/images/import_xml.svg';
import { useToast } from '../../context/ToastContext';
const PAGE_SIZE = 10;
const getToolboxToken = (): string => localStorage.getItem('toolboxToken') || '';
const getNcToken      = () => localStorage.getItem('amsToken')     || '';

interface NowCertsClient {
  name: string;
  databaseId: string;
  eMail?: string;
  insuredType?: number;
}

const STATUS_CONFIG: Record<number, { label: string; color: string }> = {
  0: { label: 'Submitted',     color: '#fbbf24' },
  1: { label: 'In Progress',   color: '#a78bfa' },
  2: { label: 'Done',          color: '#34d399' },
  3: { label: 'Failed',        color: '#f87171' },
  4: { label: 'Not Supported', color: '#9ca3af' },
};

const formatDate = (d: string) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  } catch { return d; }
};

interface CommissionTransaction {
  transactionId: string;
  fileName: string;
  created: string;
  userName: string;
  status: number;
  llmResponse?: string;
  isLiked?: boolean | null;
  totalCount?: number;
}

interface PreviewRow {
  insured?: string;
  policyNumber?: string;
  effectiveDate?: string;
  transactionDate?: string;
  transactionType?: string;
  premium?: number | string;
  commissionRate?: number | string;
  commissionAmount?: number | string;
  statementDate?: string;
  carrierName?: string;
}

interface PreviewData {
  totalItems?: number;
  totalCount?: number;
  totalGrossAmount?: number;
  totalCommissionAmount?: number;
  statementDate?: string;
  fileUrl?: string;
  documentUrl?: string;
  data?: PreviewRow[];
  items?: PreviewRow[];
}

const PREVIEW_PAGE_SIZE = 10;

const fmtCurrency = (v?: number | string) => {
  const n = Number(v);
  if (isNaN(n)) return '—';
  return (n < 0 ? '-$' : '$') + Math.abs(n).toFixed(2);
};


interface HistoryProps {
  autoOpenFeedbackTxId?: string;
  onAutoOpenHandled?: () => void;
}

const CommissionScanHistory: React.FC<HistoryProps> = ({ autoOpenFeedbackTxId, onAutoOpenHandled }) => {
  const navigate = useNavigate();
  const [rows, setRows]         = useState<CommissionTransaction[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [downloadingId, setDownloadingId]   = useState<string | null>(null);
  const [previewOpen, setPreviewOpen]       = useState(false);
  const [previewTx, setPreviewTx]            = useState<CommissionTransaction | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData]       = useState<PreviewData | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl]         = useState<string | null>(null);
  const [previewDataPage, setPreviewDataPage] = useState(1);
  const [stmtDate, setStmtDate]             = useState('');
  const [llmResponseData, setLlmResponseData] = useState<any[]>([]);
  const [columnKeys, setColumnKeys]           = useState<string[]>([]);
  const [totalGrossAmount, setTotalGrossAmount]         = useState<number>(0);
  const [totalCommissionAmount, setTotalCommissionAmount] = useState<number>(0);
  const [importCommissionLoading, setImportCommissionLoading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage]           = useState('');
  const [importXMLLoading, setImportXMLLoading]               = useState(false);
  const [xmlErrorMessage, setXmlErrorMessage]                 = useState('');

  const [feedbackTxId, setFeedbackTxId]       = useState<string | null>(null);
  const [feedbackText, setFeedbackText]       = useState('');
  const [feedbackRating, setFeedbackRating]   = useState<boolean | null>(null);
  const [feedbackAnchor, setFeedbackAnchor]   = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError, setFeedBackError]     = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  const { showSuccess } = useToast();
  const fetchData = useCallback(async (pageNum: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        pageNumber: String(pageNum),
        recordCount: String(PAGE_SIZE),
      });
      const res = await fetch(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/commissionTransactions?${params}`,
        { headers: { Authorization: `Bearer ${getToolboxToken()}`, accept: 'application/json' }, signal },
      );
      if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
      const json = await res.json();
      const data: CommissionTransaction[] = Array.isArray(json)
        ? json
        : (json.data ?? json.transactions ?? json.items ?? []);
      setRows(data);
      setTotalCount(data[0]?.totalCount ?? 0);
    } catch (e: any) {
      if (e.name === 'AbortError') return;
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let controller: AbortController | undefined;
    const timer = setTimeout(() => {
      controller = new AbortController();
      fetchData(page, controller.signal);
    }, 0);
    return () => { clearTimeout(timer); controller?.abort(); };
  }, [fetchData, page]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.fileName?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPdfBlobUrl(prev => { if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev); return null; });
    setPreviewTx(null);
    setPreviewData(null);
  }, []);

  const onPreviewClick = useCallback(async (tx: CommissionTransaction) => {
    setPreviewTx(tx);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setPdfBlobUrl(null);
    setPreviewDataPage(1);

    const parsed: any[] = tx.llmResponse ? (() => { try { return JSON.parse(tx.llmResponse); } catch { return []; } })() : [];
    setLlmResponseData(parsed);

    const today = new Date().toISOString().split('T')[0];
    const statDate = parsed[0]?.Statement_Date ?? today;
    setStmtDate(statDate);

    try {
      const res = await fetch(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/previewdocument`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToolboxToken()}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({ transactionId: tx.transactionId, fileName: tx.fileName }),
      });
      if (!res.ok) throw new Error(`Preview failed (${res.status})`);
      const json = await res.json();

      const pdfUrl: string | null = json.pdfUrl ?? json.data?.pdfUrl ?? null;
      setPdfBlobUrl(pdfUrl);

      const rawCsv: string = json.csvData ?? json.data?.csvData ?? '[]';
      const parsedCsvData: any[] = (() => { try { return JSON.parse(rawCsv); } catch { return []; } })();

      setLlmResponseData(parsedCsvData);
      setColumnKeys(parsedCsvData.length > 0 ? Object.keys(parsedCsvData[0]) : []);
      setTotalGrossAmount(
        parsedCsvData.reduce((sum: number, row: any) =>
          sum + (parseFloat(String(row.Premium ?? 0).replace(/[$,]/g, '')) || 0), 0),
      );
      setTotalCommissionAmount(
        parsedCsvData.reduce((sum: number, row: any) =>
          sum + (parseFloat(String(row.Commission_Amount ?? 0).replace(/[$,]/g, '')) || 0), 0),
      );

      if (json.statementDate) setStmtDate(json.statementDate);
    } catch { /* preview failed silently */ }
    finally { setPreviewLoading(false); }
  }, []);

  const onDownloadClick = useCallback(async (documentType: string, tx: CommissionTransaction) => {
    const key = `${documentType}-${tx.transactionId}`;
    setDownloadingId(key);
    try {
      let statementDate = '';
      if (tx.llmResponse) {
        try {
          const parsed = JSON.parse(tx.llmResponse);
          statementDate = parsed[0]?.Statement_Date ?? '';
        } catch { /* ignore malformed llmResponse */ }
      }
      const res = await fetch(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/document/${documentType}/${tx.transactionId}/${statementDate}`,
        { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const baseName = tx.fileName.replace(/\.[^.]+$/, '');
      a.href = url;
      a.download = `${baseName}.${documentType}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* errors are silent — buttons simply re-enable */
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const onUploadToAMSClicked = useCallback((transactionId: string) => {
    setImportCommissionLoading(true);
    setUploadErrorMessage('');
    const nowCertsToken = getNcToken();
    axios({
      method: 'get',
      url: `${(process.env.REACT_APP_DataFabric_Url || '')}/api/ncaicommissionstatement/upload/${transactionId}?token=${nowCertsToken}`,
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(response => {
        window.open(response.data.redirectURL, '_blank');
        setImportCommissionLoading(false);
      })
      .catch(error => {
        setImportCommissionLoading(false);
        if (error.response?.status === 401) {
          navigate('/');
        } else {
          setUploadErrorMessage('Error while uploading the file.');
          setTimeout(() => setUploadErrorMessage(''), 3000);
        }
      });
  }, [navigate]);

  const onUploadXMLToAMSClicked = useCallback((tx: CommissionTransaction) => {
    let statementDate = '';
    if (tx.llmResponse) {
      try {
        const parsed = JSON.parse(tx.llmResponse);
        statementDate = parsed[0]?.Statement_Date ?? '';
      } catch { /* ignore */ }
    }

    setImportXMLLoading(true);
    setXmlErrorMessage('');
    const nowCertsToken = getNcToken();
    axios({
      method: 'get',
      url: `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/upload/xml/${tx.transactionId}?token=${nowCertsToken}&statementDate=${statementDate}`,
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(response => {
        if (response.data.success === true) {
          window.open(response.data.redirectURL, '_blank');
        } else {
          setXmlErrorMessage(response.data.errorMessage || 'Error while uploading the XML file.');
          setTimeout(() => setXmlErrorMessage(''), 3000);
        }
        setImportXMLLoading(false);
      })
      .catch(error => {
        setImportXMLLoading(false);
        if (error.response?.status === 401) {
          navigate('/');
        } else {
          setXmlErrorMessage('Error while uploading the XML file.');
          setTimeout(() => setXmlErrorMessage(''), 3000);
        }
      });
  }, [navigate]);

  /* ── Feedback handlers ── */
  const openFeedback = useCallback((tx: CommissionTransaction, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(tx.transactionId);
    setFeedbackText('');
    setFeedbackRating(tx.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const onSaveRatings = useCallback((txId: string, isLiked: boolean | null, feedback: string) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 1, TransactionId: txId, IsLiked: isLiked, Feedback: feedback ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    ).then(() => {
      showSuccess('Feedback submitted successfully!');
      closeFeedback();
    }).catch((error: any) => {
      if (error.response?.status === 401) navigate('/');
    });
  }, [navigate, closeFeedback]);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    const currentRow = rows.find(r => r.transactionId === feedbackTxId);
    const newLiked: boolean | null = currentRow?.isLiked === true ? null : true;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: newLiked } : r));
    closeFeedback();
    onSaveRatings(feedbackTxId, newLiked, feedbackText);
  }, [feedbackTxId, rows, feedbackText, onSaveRatings, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    const currentRow = rows.find(r => r.transactionId === feedbackTxId);
    const newLiked: boolean | null = currentRow?.isLiked === false ? null : false;
    setRows(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: newLiked } : r));
    closeFeedback();
    onSaveRatings(feedbackTxId, newLiked, feedbackText);
  }, [feedbackTxId, rows, feedbackText, onSaveRatings, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) {
      setFeedBackError('Please enter your feedback');
      return;
    }
    if (feedbackText.length > 250) {
      setFeedBackError('Please provide your feedback within 250 characters');
      return;
    }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    const currentRow = rows.find(r => r.transactionId === feedbackTxId);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 1, TransactionId: feedbackTxId, IsLiked: currentRow?.isLiked ?? feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => {
        setFeedbackSubmitting(false);
        showSuccess('Feedback submitted successfully!');
        closeFeedback();
      })
      .catch((error: any) => {
        setFeedbackSubmitting(false);
        if (error.response?.status === 401) navigate('/');
      });
  }, [feedbackTxId, feedbackText, feedbackRating, rows, closeFeedback, navigate]);

  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) {
        closeFeedback();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);

  useEffect(() => {
    if (!autoOpenFeedbackTxId || rows.length === 0) return;
    const tx = rows.find(r => r.transactionId === autoOpenFeedbackTxId);
    if (!tx) return;
    setFeedbackTxId(tx.transactionId);
    setFeedbackText('');
    setFeedbackRating(tx.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: 200, right: 50 });
    onAutoOpenHandled?.();
  }, [autoOpenFeedbackTxId, rows, onAutoOpenHandled]);

  /* ── derived preview data ── */
  const previewRows: any[] = previewData?.data ?? previewData?.items ?? llmResponseData;
  const previewTotalPages = Math.max(1, Math.ceil(previewRows.length / PREVIEW_PAGE_SIZE));
  const previewPaginated  = previewRows.slice((previewDataPage - 1) * PREVIEW_PAGE_SIZE, previewDataPage * PREVIEW_PAGE_SIZE);

  return (
  <>
    <motion.div
      className="qp-history"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* Header */}
      <div className="qp-history__header">
        <h3 className="qp-history__title">Commission Scan History</h3>
        <div className="csh-header-actions">
          <div className="qp-history__search">
            <FiSearch size={13} />
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <motion.button
            className="csh-refresh-btn"
            onClick={() => fetchData(page)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
            Refresh
          </motion.button>
        </div>
      </div>

      {/* Body */}
      <div className="qp-history__body">
        {error ? (
          <div className="qp-empty-state csh-empty-error">
            <FiFileText size={28} />
            <p>{error}</p>
          </div>
        ) : loading ? (
          <div className="qp-history-loader csh-history-loader--min">
            <div className="qp-history-loader__orb">
              <div className="qp-history-loader__ring qp-history-loader__ring--1" />
              <div className="qp-history-loader__ring qp-history-loader__ring--2" />
              <div className="qp-history-loader__core"><FiClock size={16} /></div>
            </div>
            <div className="qp-history-loader__text">
              <p className="qp-history-loader__title">Loading History</p>
              <p className="qp-history-loader__sub">
                Fetching commission scan records
                <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
              </p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="qp-empty-state">
            <FiFileText size={28} />
            <p>{search ? 'No records match your search' : 'No commission scans yet.'}</p>
          </div>
        ) : (
          <div className="qp-table-wrap glass-surface">
            <table className="qp-table">
              <thead>
                <tr>
                  <th className="qp-table__th">File Name</th>
                  <th className="qp-table__th">Submitted Date</th>
                  <th className="qp-table__th">Username</th>
                  <th className="qp-table__th">Status</th>
                  <th className="qp-table__th csh-text-center">Actions</th>
                  <th className="qp-table__th csh-text-center">Feedback</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tx: CommissionTransaction, i: number) => {
                  const s = STATUS_CONFIG[tx.status ?? 0];
                  return (
                    <tr key={tx.transactionId ?? i} className="qp-table__row">
                      <td
                        className="qp-table__td csh-td-filename"
                      >
                        {tx.fileName}
                      </td>
                      <td className="qp-table__td csh-td-nowrap">
                        {formatDate(tx.created ?? '')}
                      </td>
                      <td className="qp-table__td">{tx.userName}</td>
                      <td className="qp-table__td">
                        {s && <span className="qp-status-pill" style={{ color: s.color }}>{s.label}</span>}
                      </td>
                      <td className="qp-table__td csh-text-center">
                        <div className="qp-table__actions">
                          <button
                            className="qp-table__action-btn"
                            title="Download CSV"
                            disabled={downloadingId === `csv-${tx.transactionId}`}
                            onClick={() => onDownloadClick('csv', tx)}
                            style={{ opacity: downloadingId === `csv-${tx.transactionId}` ? 0.45 : 1 }}
                          >
                            <img src={CSVDownload} alt="Download CSV" width={30} height={30} />
                          </button>
                          <button
                            className="qp-table__action-btn"
                            title="Download XML"
                            disabled={downloadingId === `xml-${tx.transactionId}`}
                            onClick={() => onDownloadClick('xml', tx)}
                            style={{ opacity: downloadingId === `xml-${tx.transactionId}` ? 0.45 : 1 }}
                          >
                            <img src={XMLDownload} alt="Download XML" width={30} height={30} />
                          </button>
                          <button className="qp-table__action-btn" title="Preview" onClick={() => onPreviewClick(tx)}>
                            <img src={PrivewIcon} alt="Preview" width={30} height={30} />
                          </button>
                          <button
                            className="qp-table__action-btn"
                            title="Import CSV"
                            disabled={importCommissionLoading}
                            onClick={() => onUploadToAMSClicked(tx.transactionId)}
                          >
                            <img src={ImportCSVIcon} alt="Import CSV" width={30} height={30} />
                          </button>
                          <button
                            className="qp-table__action-btn"
                            title="Import XML"
                            disabled={importXMLLoading}
                            onClick={() => onUploadXMLToAMSClicked(tx)}
                          >
                            <img src={ImportXMLIcon} alt="Import XML" width={30} height={30} />
                          </button>
                        </div>
                      </td>
                      <td className="qp-table__td csh-text-center">
                        <button
                          className="qp-table__action-btn csh-btn-centered"
                          title="Feedback"
                          onClick={e => openFeedback(tx, e)}
                        >
                          <FiMessageSquare size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="qp-pagination">
            <span className="qp-pagination__info">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
            </span>
            <div className="qp-pagination__controls">
              <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
              <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
              {pageList.map((p, i) =>
                p === '…'
                  ? <span key={`ellipsis-${i}`} className="qp-pagination__ellipsis">…</span>
                  : <button
                      key={p}
                      className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                      onClick={() => setPage(p as number)}
                    >{p}</button>
              )}
              <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
              <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
            </div>
          </div>
        )}
      </div>
    </motion.div>

    {/* ── Feedback Popup ── */}
    {feedbackTxId && feedbackAnchor && (
      <div
        ref={feedbackRef}
        className="csfb-popup"
        style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
      >
        <h4 className="csfb-title">Feedback</h4>
        <textarea
          className="csfb-textarea"
          placeholder="Enter your feedback here"
          value={feedbackText}
          onChange={e => setFeedbackText(e.target.value)}
          rows={4}
        />
        <div className="csfb-rating">
          <button
            className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
            onClick={onThumbsUpClick}
            title="Thumbs up"
          >
            <FiThumbsUp size={20} />
          </button>
          <button
            className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
            onClick={onThumbsDownClick}
            title="Thumbs down"
          >
            <FiThumbsDown size={20} />
          </button>
        </div>
        {feedBackError && (
          <p className="csfb-error">{feedBackError}</p>
        )}
        <div className="csfb-actions">
          <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
          <button
            className="csfb-btn-submit"
            disabled={feedbackSubmitting}
            onClick={onSubmitFeedbackClick}
          >
            {feedbackSubmitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    )}


    {/* ── Preview Dialog (PrimeReact) ── */}
    <Dialog
      visible={previewOpen}
      onHide={closePreview}
      header={
        <div className="cspr-header-row">
          <div className="cspr-stats-bar cspr-stats-bar--flex">
            <span><strong>Total Items:</strong> {llmResponseData.length || '—'}</span>
            <span><strong>Total Gross Amount:</strong> {fmtCurrency(totalGrossAmount)}</span>
            <span><strong>Total Commission Amount:</strong> {fmtCurrency(totalCommissionAmount)}</span>
          </div>
          <button
            onClick={closePreview}
            className="cspr-close-btn"
          >✕</button>
        </div>
      }
      closable={false}
      style={{ width: '92vw', maxWidth: 1500 }}
      contentStyle={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '80vh' }}
      maximizable
      draggable={false}
      resizable={false}
      modal
      className="cspr-dialog"
    >
      {/* Body */}
      <div className="cspr-body">

        {/* Left — PDF */}
        <div className="cspr-pdf-panel">
          <div className="cspr-pdf-panel__header">
            <span className="cspr-pdf-panel__title">Uploaded PDF</span>
            <button
              className="cspr-action-btn cspr-action-btn--purple"
              disabled={!previewTx}
              onClick={() => previewTx && onUploadToAMSClicked(previewTx.transactionId)}
            >
              Upload In AMS
            </button>
          </div>
          <div className="cspr-pdf-viewer">
            {pdfBlobUrl
              ? <iframe src={`${pdfBlobUrl}#zoom=90`} title="PDF Preview" className="cspr-pdf-iframe" />
              : previewLoading
                ? <div className="cspr-pdf-placeholder">Loading PDF…</div>
                : <div className="cspr-pdf-placeholder">PDF not available</div>
            }
          </div>
        </div>

        {/* Right — Extracted data */}
        <div className="cspr-data-panel">
          {/* Controls */}
          <div className="cspr-data-controls">
            <label className="cspr-label">
              Statement Date <span className="cspr-required">*</span>
              <input
                className="cspr-date-input"
                type="date"
                value={stmtDate}
                onChange={e => setStmtDate(e.target.value)}
              />
            </label>
            <button
              className="cspr-action-btn cspr-action-btn--purple"
              disabled={!previewTx}
              onClick={() => previewTx && onUploadToAMSClicked(previewTx.transactionId)}
            >
              Import Commissions
            </button>
            <button
              className="cspr-action-btn cspr-action-btn--purple"
              disabled={importXMLLoading || !previewTx}
              onClick={() => previewTx && onUploadXMLToAMSClicked(previewTx)}
            >
              {importXMLLoading ? 'Importing…' : 'Import XML'}
            </button>
          </div>

          {uploadErrorMessage && (
            <p className="csh-upload-error-text">
              {uploadErrorMessage}
            </p>
          )}

          <p className="cspr-data-title">Extracted data</p>

          {previewLoading ? (
            <div className="cspr-loading">Loading extracted data…</div>
          ) : previewRows.length === 0 ? (
            <div className="cspr-loading">No data available.</div>
          ) : (
            <>
              <div className="cspr-table-wrap">
                <table className="cspr-table">
                  <thead>
                    <tr>
                      {columnKeys.map(k => (
                        <th key={k} className="cspr-th">{k.replace(/_/g, ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewPaginated.map((row, i) => (
                      <tr key={i} className="cspr-tr">
                        {columnKeys.map(k => (
                          <td key={k} className="cspr-td">{row[k] ?? '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {previewTotalPages > 1 && (
                <div className="cspr-pagination">
                  <button className="cspr-page-btn" disabled={previewDataPage === 1} onClick={() => setPreviewDataPage(1)}>«</button>
                  <button className="cspr-page-btn" disabled={previewDataPage === 1} onClick={() => setPreviewDataPage(p => p - 1)}>‹</button>
                  {Array.from({ length: previewTotalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === previewTotalPages || Math.abs(p - previewDataPage) <= 1)
                    .reduce<(number|'…')[]>((acc, p, idx, arr) => {
                      if (idx > 0 && (p as number) - (arr[idx-1] as number) > 1) acc.push('…');
                      acc.push(p); return acc;
                    }, [])
                    .map((p, i) => p === '…'
                      ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                      : <button key={p} className={`cspr-page-btn${previewDataPage === p ? ' cspr-page-btn--active' : ''}`} onClick={() => setPreviewDataPage(p as number)}>{p}</button>
                    )}
                  <button className="cspr-page-btn" disabled={previewDataPage === previewTotalPages} onClick={() => setPreviewDataPage(p => p + 1)}>›</button>
                  <button className="cspr-page-btn" disabled={previewDataPage === previewTotalPages} onClick={() => setPreviewDataPage(previewTotalPages)}>»</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Dialog>
  </>
  );
};

export default CommissionScanHistory;

